import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useT } from '@/lib/i18n';

function resolveEmbed(raw) {
    if (! raw) return null;
    const url = String(raw).trim();

    const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/);
    if (yt) {
        return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0&modestbranding=1` };
    }

    const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vimeo) {
        return { type: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1` };
    }

    if (/\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(url)) {
        return { type: 'video', src: url };
    }

    return { type: 'iframe', src: url };
}

export function HeroVideoModal({ open, onClose, url, title }) {
    const { t } = useT();
    const embed = useMemo(() => resolveEmbed(url), [url]);

    useEffect(() => {
        if (! open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose?.();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose]);

    if (! open || ! embed) return null;

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label={title || t('common.ui.hero_video.title')}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm animate-in fade-in duration-150"
            onClick={onClose}
        >
            <button
                type="button"
                onClick={onClose}
                aria-label={t('common.ui.hero_video.close', 'Close video')}
                className="absolute right-5 top-5 grid size-10 place-items-center rounded-full border border-white/15 bg-white/5 text-white/90 backdrop-blur transition-colors hover:border-white/40 hover:bg-white/10"
            >
                <X className="size-5" />
            </button>

            <div
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-5xl overflow-hidden rounded-2xl bg-black shadow-2xl animate-in zoom-in-95 duration-200"
            >
                <div className="relative aspect-video w-full">
                    {embed.type === 'iframe' ? (
                        <iframe
                            src={embed.src}
                            title={title || t('common.ui.hero_video.title')}
                            className="absolute inset-0 size-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <video
                            src={embed.src}
                            controls
                            autoPlay
                            playsInline
                            className="absolute inset-0 size-full object-contain"
                        />
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
